import React, { useCallback, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';

import { env } from 'env';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { useMytaverseEvent } from '@core/dashboard/providers';
import BrandingsService from '@services/BrandingsService';
import type { IBrandingEntityValues } from '@interfaces/rooms';

import {
  buildBrandingEntitiesCsv,
  IBrandingCsvImportPlan,
  IBrandingCsvEntity,
  parseBrandingEntitiesCsv,
} from '../brandingEntitiesCsv';
import { useEditorMode } from '../EditorModeContext';
import { useInspectorStyles } from '../Inspector/styles';
import ObjectsCsvImportModal from './ObjectsCsvImportModal';

// Matches the inspector's page: a map's configurations comfortably fit, and one
// falling outside the page reads as a failed load rather than as "no overrides".
const BRANDINGS_PAGE_SIZE = 100;

/**
 * Reads a picked file as text.
 *
 * Uses `FileReader` rather than `Blob.text()`, which the jsdom the tests run on
 * does not implement.
 *
 * @param file - File chosen in the import input.
 * @returns The file's content.
 */
const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsText(file);
  });

interface IObjectsCsvActionsProps {
  entities: IBrandingCsvEntity[];
}

/**
 * Exports the Objects catalogue as CSV and takes an edited one back.
 *
 * Both directions work off the whole catalogue rather than the selected object,
 * so a configuration moves in one file. The export doubles as the template: a
 * row per declared property, overridden or not.
 *
 * @param props.entities - Rows of the objects panel: what the build declares,
 * plus whatever Unreal reports standing.
 */
const ObjectsCsvActions = ({ entities }: IObjectsCsvActionsProps) => {
  const { t: translate } = useTranslation();
  const styles = useInspectorStyles();
  const { showNotification } = useNotification();
  const { currentRoom, currentBrandingId } = useMytaverseEvent();
  const { bumpBrandingEntitiesTick } = useEditorMode();

  const [isBusy, setIsBusy] = useState(false);
  const [plan, setPlan] = useState<IBrandingCsvImportPlan | null>(null);

  const mapId = currentRoom?.map?.mapId;
  const isEditingLive =
    !!currentBrandingId && currentBrandingId === currentRoom?.activeBrandingId;

  /**
   * Reads the values this configuration stores.
   *
   * @returns The stored values, keyed by entity then property.
   * @throws When the configuration is not among the ones the map returns.
   */
  const loadStoredValues =
    useCallback(async (): Promise<IBrandingEntityValues> => {
      const response = await BrandingsService.getBrandings({
        mapId,
        limit: BRANDINGS_PAGE_SIZE,
      });

      const branding = response.brandings?.find(
        (candidate) => candidate?.brandingId === currentBrandingId,
      );

      // Exporting or merging against values that did not come back would write a
      // file that looks like "nothing is overridden", so it is an error instead.
      if (!branding) {
        throw new Error(
          `Configuration ${currentBrandingId} not found for map ${mapId}`,
        );
      }

      return branding.brandingEntities ?? {};
    }, [currentBrandingId, mapId]);

  const handleExport = useCallback(async () => {
    if (!currentBrandingId || !mapId) {
      return;
    }

    setIsBusy(true);

    try {
      const values = await loadStoredValues();
      const csv = buildBrandingEntitiesCsv(entities, values);

      saveAs(
        new Blob([csv], { type: 'text/csv;charset=utf-8' }),
        `objects-${currentBrandingId}.csv`,
      );
    } catch (exportError) {
      console.error(
        `Failed to export branding entity values: ${
          (exportError as Error)?.message
        }`,
        { brandingId: currentBrandingId },
      );
      showNotification({
        message: translate(
          'editorMode.objectsCsvExportFailed',
          'Could not export the objects. Try again.',
        ),
        type: NOTIFICATION_TYPES.ERROR,
      });
    } finally {
      setIsBusy(false);
    }
  }, [
    currentBrandingId,
    entities,
    loadStoredValues,
    mapId,
    showNotification,
    translate,
  ]);

  const handleFileSelected = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Cleared so picking the same file again still fires a change event.
      event.target.value = '';

      if (!file || !currentBrandingId || !mapId) {
        return;
      }

      setIsBusy(true);

      try {
        const [text, values] = await Promise.all([
          readFileAsText(file),
          loadStoredValues(),
        ]);

        setPlan(
          parseBrandingEntitiesCsv(
            text,
            entities,
            values,
            env.REACT_APP_API_URL,
          ),
        );
      } catch (readError) {
        console.error(
          `Failed to read the objects CSV: ${(readError as Error)?.message}`,
          { brandingId: currentBrandingId },
        );
        showNotification({
          message: translate(
            'editorMode.objectsCsvReadFailed',
            'Could not read the file. Try again.',
          ),
          type: NOTIFICATION_TYPES.ERROR,
        });
      } finally {
        setIsBusy(false);
      }
    },
    [
      currentBrandingId,
      entities,
      loadStoredValues,
      mapId,
      showNotification,
      translate,
    ],
  );

  const exportLabel = translate(
    'editorMode.objectsCsvExport',
    'Export objects as CSV',
  );
  const importLabel = translate(
    'editorMode.objectsCsvImport',
    'Import objects from CSV',
  );

  return (
    <>
      <Box
        sx={styles.brandingPropertyUploadRow}
        // The buttons sit in the group header, whose own click collapses the
        // group; without this every export would fold the tree.
        onClick={(event) => event.stopPropagation()}
      >
        <Tooltip title={exportLabel} placement="bottom" enterDelay={400}>
          <Box component="span">
            <IconButton
              size="small"
              disabled={isBusy || !currentBrandingId}
              onClick={handleExport}
              sx={styles.brandingPropertyResetButton}
              aria-label={exportLabel}
              data-testid="scene-tree-objects-export-btn"
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Tooltip>

        <Tooltip title={importLabel} placement="bottom" enterDelay={400}>
          <Box component="span">
            <IconButton
              size="small"
              component="label"
              disabled={isBusy || !currentBrandingId}
              sx={styles.brandingPropertyResetButton}
              aria-label={importLabel}
              data-testid="scene-tree-objects-import-btn"
            >
              <FileUploadOutlinedIcon sx={{ fontSize: 15 }} />
              <input
                hidden
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileSelected}
                data-testid="scene-tree-objects-import-input"
              />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>

      <ObjectsCsvImportModal
        open={!!plan}
        plan={plan}
        brandingId={currentBrandingId ?? null}
        isEditingLive={isEditingLive}
        onClose={() => setPlan(null)}
        onImported={bumpBrandingEntitiesTick}
      />
    </>
  );
};

export default ObjectsCsvActions;
